/** Vendor card links carry a public vendor code, never a PIN or session. */
export function vendorCodeFromLink(raw: string): string | null {
	try {
		const url = new URL(raw);
		const https = url.protocol === 'https:' && url.hostname === 'app.scandeckapp.com'
			&& ['', '443'].includes(url.port) && ['/', '/open', '/open/'].includes(url.pathname);
		const scheme = url.protocol === 'scandeck:' && url.hostname === 'vendor'
			&& !url.port && ['', '/'].includes(url.pathname);
		if ((!https && !scheme) || url.username || url.password || url.hash) return null;
		// Reject ambiguous/credential-bearing links instead of partially consuming them.
		if ([...url.searchParams.keys()].some((key) => key !== 'v' && key !== 'vendor')) return null;
		const codes = [...url.searchParams.getAll('v'), ...url.searchParams.getAll('vendor')];
		if (codes.length !== 1) return null;
		const code = codes[0].trim().toUpperCase();
		return /^[A-Z0-9-]{3,20}$/.test(code) ? code : null;
	} catch {
		return null;
	}
}

export interface VendorLinkPlugin {
	addListener(event: 'appUrlOpen', callback: (event: { url: string }) => void):
		Promise<{ remove(): Promise<void> }>;
	getLaunchUrl(): Promise<{ url: string } | undefined>;
}

/** Listener first, then cold URL. Late cleanup and stale launch responses are safe. */
export function subscribeVendorLinks(plugin: VendorLinkPlugin, receive: (code: string) => void): () => void {
	let alive = true;
	let receivedLiveLink = false;
	let handle: { remove(): Promise<void> } | undefined;
	let lastCode = '';
	let lastAt = 0;
	const accept = (url: string) => {
		if (!alive) return;
		const code = vendorCodeFromLink(url);
		if (!code || (code === lastCode && Date.now() - lastAt < 1000)) return;
		lastCode = code;
		lastAt = Date.now();
		receive(code);
	};
	void (async () => {
		handle = await plugin.addListener('appUrlOpen', ({ url }) => {
			if (vendorCodeFromLink(url)) receivedLiveLink = true;
			accept(url);
		});
		if (!alive) { await handle.remove(); return; }
		const launch = await plugin.getLaunchUrl();
		if (!receivedLiveLink && launch) accept(launch.url);
	})().catch(() => {
		// Do not log credential-bearing incoming URLs to telemetry.
		console.warn('[vendor-links] Native link listener unavailable');
	});
	return () => {
		alive = false;
		if (handle) void handle.remove().catch(() => {});
	};
}
